import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import { CALCULATORS } from '../marketing/data';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const meta: Record<string, [string, string]> = {
  investment: ['Investment', C.accent],
  compliance: ['Compliance', C.ink],
  tax: ['Tax', C.accent],
};

const CalcIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="8" y2="10" />
    <line x1="12" y1="10" x2="12" y2="10" />
    <line x1="16" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="8" y2="18" />
  </svg>
);

const CalculatorsIndexPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Landlord Calculators — 10 Free Tools | MillOS',
    description: 'Ten free calculators for UK landlords: rental yield, stamp duty, ROI, BTL mortgage, EPC expiry, tax estimator and portfolio health score.',
    canonicalPath: '/calculators',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="Free tools"
        title={<>Landlord <Box component="em" sx={{ fontStyle: 'italic' }}>Calculators</Box></>}
        body="Ten free, instant calculators for yield, tax, cash flow and compliance — no sign-up required."
      />
      <Box component="section" sx={{ padding: { xs: '48px 16px 64px', md: '56px 52px 96px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: '20px' }}>
          {CALCULATORS.map((c) => {
            const [label, badgeColor] = meta[c.category] || meta.investment;
            return (
              <Box
                key={c.slug}
                component={RouterLink}
                to={`/calculators/${c.slug}`}
                sx={{ display: 'flex', flexDirection: 'column', gap: '16px', background: C.panel, borderRadius: '24px', padding: '28px', height: '100%', '&:hover': { background: C.tintBlue } }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '13px', background: badgeColor, display: 'grid', placeItems: 'center' }}>
                    <CalcIcon />
                  </Box>
                  <Box sx={{ background: C.white, color: badgeColor, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '32px', padding: '4px 12px', fontSize: 11, fontWeight: 500, letterSpacing: '0.06rem', textTransform: 'uppercase' }}>
                    {label}
                  </Box>
                </Box>
                <Box component="h2" sx={{ fontFamily: F.display, fontSize: 23, lineHeight: 1.12, margin: 0 }}>{c.name}</Box>
                <Box sx={{ color: C.body, fontSize: 14, lineHeight: 1.5 }}>{c.description}</Box>
                <Box sx={{ marginTop: 'auto', fontSize: 14, fontWeight: 500, color: C.accent }}>Open calculator →</Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default CalculatorsIndexPage;
